// This church's assessments over time: an overall line and a per-section
// table with year-on-year deltas. Aggregate only — the same anonymity wall as
// the rest of the module. Sections line up by their stable slug id, so a change
// of instrument version between years still compares like with like.

import { useEffect, useState, type JSX } from 'react';
import { healthApi, type CycleDetail, type TrendPoint, type TrendResponse } from './api';
import { formatNzDateShort } from './format';

const BAND_COLOR: Record<string, string> = {
  embedded: 'var(--band-embedded)',
  strong: 'var(--band-strong)',
  developing: 'var(--band-developing)',
  priority: 'var(--band-priority)',
};

function pointLabel(p: TrendPoint): string {
  const iso = p.closes_at ?? p.opens_at;
  if (iso) {
    const d = new Date(iso);
    if (!Number.isNaN(d.getTime())) return String(d.getFullYear());
  }
  return formatNzDateShort(iso) || 'Cycle';
}

function Delta({ d }: { d: number }): JSX.Element {
  if (d === 0) {
    return <span style={{ marginLeft: 5, color: 'var(--health-grey)', fontSize: 11 }}>–</span>;
  }
  const up = d > 0;
  return (
    <span
      style={{
        marginLeft: 5,
        fontSize: 11,
        color: up ? 'var(--band-embedded)' : 'var(--band-priority)',
      }}
    >
      {up ? '▲' : '▼'}
      {Math.abs(d).toFixed(1)}
    </span>
  );
}

function OverallChart({ points }: { points: TrendPoint[] }): JSX.Element {
  const W = 520;
  const H = 170;
  const padL = 26;
  const padR = 12;
  const padT = 12;
  const padB = 26;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;
  const n = points.length;
  const x = (i: number): number => (n <= 1 ? padL + innerW / 2 : padL + (innerW * i) / (n - 1));
  const y = (v: number): number => padT + innerH * (1 - (v - 1) / 3); // scale 1..4
  const dots = points
    .map((p, i) => (p.overall_mean !== null ? { cx: x(i), cy: y(p.overall_mean), p } : null))
    .filter((d): d is { cx: number; cy: number; p: TrendPoint } => d !== null);
  const path = dots
    .map((d, i) => `${i === 0 ? 'M' : 'L'}${d.cx.toFixed(1)} ${d.cy.toFixed(1)}`)
    .join(' ');
  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      style={{ width: '100%', height: 'auto', maxWidth: 560 }}
      role="img"
      aria-label="Overall health over time"
    >
      {[1, 2, 3, 4].map((v) => (
        <g key={v}>
          <line
            x1={padL}
            x2={W - padR}
            y1={y(v)}
            y2={y(v)}
            stroke="var(--health-border, #e5e0d5)"
            strokeWidth={1}
          />
          <text
            x={padL - 6}
            y={y(v) + 3}
            textAnchor="end"
            fontSize={10}
            fill="var(--health-grey, #6b7280)"
          >
            {v}
          </text>
        </g>
      ))}
      <path d={path} fill="none" stroke="var(--health-accent, #5C7E25)" strokeWidth={2} />
      {dots.map((d) => (
        <circle
          key={d.p.cycle_id}
          cx={d.cx}
          cy={d.cy}
          r={4}
          fill={BAND_COLOR[d.p.overall_band ?? ''] ?? 'var(--health-accent)'}
        />
      ))}
      {points.map((p, i) => (
        <text
          key={p.cycle_id}
          x={x(i)}
          y={H - 8}
          textAnchor="middle"
          fontSize={10}
          fill="var(--health-grey, #6b7280)"
        >
          {pointLabel(p)}
        </text>
      ))}
    </svg>
  );
}

export function TrendTab({ detail }: { detail: CycleDetail }): JSX.Element {
  const [data, setData] = useState<TrendResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Which section row is being pointed at (for the floating trend-line popover).
  const [hover, setHover] = useState<{ id: string; title: string; rect: DOMRect } | null>(null);

  useEffect(() => {
    healthApi
      .trend(detail.cycle.id)
      .then(setData)
      .catch(() => setError('The trend could not be loaded. Please try again.'));
  }, [detail.cycle.id]);

  if (error) return <p style={{ color: 'var(--health-grey)' }}>{error}</p>;
  if (!data) return <p style={{ color: 'var(--health-grey)' }}>Loading trend...</p>;

  const analysed = data.points.filter((p) => p.overall_mean !== null);
  if (analysed.length < 2) {
    return (
      <div className="card">
        <h3 style={{ fontSize: 15, marginBottom: 6 }}>Trend over time</h3>
        <p style={{ fontSize: 13.5, color: 'var(--health-grey)', lineHeight: 1.7 }}>
          A trend appears once {data.church_name} has completed more than one assessment with enough
          responses to report.{' '}
          {analysed.length === 1
            ? 'This is the first one with results so far.'
            : 'None are ready yet.'}
        </p>
      </div>
    );
  }

  // Union of sections across every year, keyed by stable slug; later years win
  // the title/number so the ordering reflects the current instrument.
  const secMap = new Map<string, { title: string; no: number }>();
  for (const p of analysed) {
    for (const s of p.sections) secMap.set(s.id, { title: s.title, no: s.section_no });
  }
  const order = [...secMap.entries()]
    .map(([id, v]) => ({ id, title: v.title, no: v.no }))
    .sort((a, b) => a.no - b.no);

  const meanOf = (p: TrendPoint, id: string): number | null =>
    p.sections.find((s) => s.id === id)?.mean ?? null;
  const bandOf = (p: TrendPoint, id: string): string | null =>
    p.sections.find((s) => s.id === id)?.band ?? null;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 16 }}>
      <div className="card">
        <h3 style={{ fontSize: 15, marginBottom: 2 }}>Overall health over time</h3>
        <p style={{ fontSize: 12.5, color: 'var(--health-grey)', marginBottom: 10 }}>
          The average of all section scores (1 to 4) for each assessment {data.church_name} has
          completed.
        </p>
        <OverallChart points={analysed} />
      </div>

      <div className="card" style={{ padding: '8px 16px 4px' }}>
        <p style={{ fontSize: 12.5, color: 'var(--health-grey)', margin: '8px 4px 12px' }}>
          Each section&apos;s score by year, with the change on the year before. Point at a section
          to see its own trend line.
        </p>
        <div className="table-wrap">
          <table className="table trend-table">
            <thead>
              <tr>
                <th scope="col">Section</th>
                {analysed.map((p) => (
                  <th key={p.cycle_id} scope="col" style={{ textAlign: 'center' }}>
                    {pointLabel(p)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {order.map((sec) => (
                <tr
                  key={sec.id}
                  className="trend-row"
                  tabIndex={0}
                  onMouseEnter={(e) =>
                    setHover({
                      id: sec.id,
                      title: sec.title,
                      rect: e.currentTarget.getBoundingClientRect(),
                    })
                  }
                  onMouseLeave={() => setHover((h) => (h?.id === sec.id ? null : h))}
                  onFocus={(e) =>
                    setHover({
                      id: sec.id,
                      title: sec.title,
                      rect: e.currentTarget.getBoundingClientRect(),
                    })
                  }
                  onBlur={() => setHover((h) => (h?.id === sec.id ? null : h))}
                >
                  <td style={{ fontWeight: 600 }}>{sec.title}</td>
                  {analysed.map((p, i) => {
                    const prevPt = i > 0 ? analysed[i - 1] : undefined;
                    return (
                      <td key={p.cycle_id} style={{ textAlign: 'center' }}>
                        <TrendCell
                          mean={meanOf(p, sec.id)}
                          prev={prevPt ? meanOf(prevPt, sec.id) : null}
                          band={bandOf(p, sec.id)}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
              <tr style={{ borderTop: '2px solid var(--health-border, #e5e0d5)' }}>
                <td style={{ fontWeight: 700 }}>Overall</td>
                {analysed.map((p, i) => {
                  const prevPt = i > 0 ? analysed[i - 1] : undefined;
                  return (
                    <td key={p.cycle_id} style={{ textAlign: 'center' }}>
                      <TrendCell
                        mean={p.overall_mean}
                        prev={prevPt ? prevPt.overall_mean : null}
                        band={null}
                        plain
                      />
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {hover && (
        <TrendPopover
          title={hover.title}
          rect={hover.rect}
          labels={analysed.map(pointLabel)}
          values={analysed.map((p) => meanOf(p, hover.id))}
        />
      )}
    </div>
  );
}

/** One year's score in the trend grid: a fixed-width band tile with the change
 *  on the prior year in its own fixed column, so tiles and deltas line up down
 *  every year regardless of whether a delta is present. */
function TrendCell({
  mean,
  prev,
  band,
  plain,
}: {
  mean: number | null;
  prev: number | null;
  band: string | null;
  plain?: boolean;
}): JSX.Element {
  if (mean === null) return <span style={{ color: 'var(--health-grey)' }}>—</span>;
  const d = prev !== null ? Math.round((mean - prev) * 10) / 10 : null;
  return (
    <span
      style={{
        display: 'inline-grid',
        gridTemplateColumns: '34px 30px',
        alignItems: 'center',
        columnGap: 4,
      }}
    >
      <span
        style={{
          textAlign: 'center',
          padding: '2px 0',
          borderRadius: 5,
          background: plain ? 'transparent' : (BAND_COLOR[band ?? ''] ?? 'transparent'),
          color: plain ? 'var(--health-ink)' : '#fff',
          fontWeight: 700,
          fontSize: 12.5,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {mean.toFixed(1)}
      </span>
      <span style={{ textAlign: 'left', fontVariantNumeric: 'tabular-nums' }}>
        {d !== null ? <Delta d={d} /> : null}
      </span>
    </span>
  );
}

/** A small line of one section's score across the years, for the hover popover. */
function SectionSparkline({
  values,
  labels,
}: {
  values: (number | null)[];
  labels: string[];
}): JSX.Element {
  const W = 220;
  const H = 74;
  const padL = 8;
  const padR = 8;
  const padT = 10;
  const padB = 20;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;
  const n = values.length;
  const x = (i: number): number => (n <= 1 ? padL + innerW / 2 : padL + (innerW * i) / (n - 1));
  const y = (v: number): number => padT + innerH * (1 - (v - 1) / 3);
  const dots = values
    .map((v, i) => (v !== null ? { cx: x(i), cy: y(v), i } : null))
    .filter((d): d is { cx: number; cy: number; i: number } => d !== null);
  const path = dots
    .map((d, k) => `${k === 0 ? 'M' : 'L'}${d.cx.toFixed(1)} ${d.cy.toFixed(1)}`)
    .join(' ');
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} role="img" aria-label="Section trend">
      {[1, 4].map((v) => (
        <line
          key={v}
          x1={padL}
          x2={W - padR}
          y1={y(v)}
          y2={y(v)}
          stroke="var(--health-border, #e5e0d5)"
          strokeWidth={1}
        />
      ))}
      <path d={path} fill="none" stroke="var(--health-accent, #5c7e25)" strokeWidth={2} />
      {dots.map((d) => (
        <circle key={d.i} cx={d.cx} cy={d.cy} r={3} fill="var(--health-accent, #5c7e25)" />
      ))}
      {values.map((v, i) =>
        v !== null ? (
          <text
            key={i}
            x={x(i)}
            y={H - 6}
            textAnchor="middle"
            fontSize={8.5}
            fill="var(--health-grey, #6b7280)"
          >
            {labels[i]}
          </text>
        ) : null,
      )}
    </svg>
  );
}

/** Floating trend-line card, positioned beside the hovered row (viewport-fixed
 *  so it is never clipped by the table's horizontal scroll container). */
function TrendPopover({
  title,
  rect,
  labels,
  values,
}: {
  title: string;
  rect: DOMRect;
  labels: string[];
  values: (number | null)[];
}): JSX.Element {
  const W = 244;
  const H = 132;
  let left = rect.right + 12;
  if (left + W > window.innerWidth - 8) left = rect.left - W - 12;
  if (left < 8) left = 8;
  let top = rect.top - 4;
  if (top + H > window.innerHeight - 8) top = window.innerHeight - H - 8;
  if (top < 8) top = 8;
  return (
    <div
      role="tooltip"
      style={{
        position: 'fixed',
        left,
        top,
        width: W,
        zIndex: 1000,
        background: 'var(--color-panel, #fff)',
        border: '1px solid var(--health-border, #e5e0d5)',
        borderRadius: 8,
        boxShadow: 'var(--shadow-pop, 0 8px 30px rgba(0, 0, 0, 0.18))',
        padding: '10px 12px',
        pointerEvents: 'none',
      }}
    >
      <p style={{ fontSize: 12, fontWeight: 700, marginBottom: 4, lineHeight: 1.35 }}>{title}</p>
      <SectionSparkline values={values} labels={labels} />
    </div>
  );
}
